import React from "react";
import { Button, Column, Conditional, Else, Grid, If, Row, Rule, useOpenToggle } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	BooleanButtonInput,
	BooleanField,
	BooleanFieldInputProps,
	DateTimeField,
	FormType,
	Label,
	RadioField,
	SubFormField,
	SubFormFieldContentProps,
	SubFormFieldItemsProps,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
	useFormState,
	ValidationDisplayPolicy,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import {
	Contract,
	Invoice,
	InvoiceOrderField,
	InvoiceStatus,
	OrderDirection,
	Student,
	useInvoicePaymentInformationListQuery,
} from "../../../Utilities";
import { InvoicePaymentSelection } from "../../Components/Invoice/PaymentSelection";
import { PaymentFormValues, PaymentLineItemFormValues } from "../../FormUtilities";
import { CurrencyField } from "../_General";
import { GuardianSelectField } from "../Guardian";
import { PaymentLineItemForm } from "../PaymentLineItem";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import styles from "./field.module.css";

/**
 * Generic props for fields of the Payment model.
 */
type FieldProps<K extends keyof PaymentFormValues.Base = keyof PaymentFormValues.Base> = BaseFieldProps<
	Pick<PaymentFormValues.Base, K>
>;

/**
 * Generic props for fields of the Payment model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof PaymentFormValues.Create = keyof PaymentFormValues.Create> = BaseFieldProps<
	Pick<PaymentFormValues.Create, K>
>;

/**
 * Field component for the `isCustomPayer` field of the Payment model.
 */
export function IsCustomPayer({ formState, noHeader = true, ...props }: FieldProps<"isCustomPayer">) {
	return (
		<BooleanField
			formState={formState}
			name="isCustomPayer"
			Input={IsCustomPayerInput}
			noHeader={noHeader}
			{...props}
		/>
	);
}

function IsCustomPayerInput(props: BooleanFieldInputProps) {
	return <BooleanButtonInput {...props}>Use Custom Payer</BooleanButtonInput>;
}

/**
 * Field component for the `customPayerName` field of the Payment model.
 */
export function CustomPayerName({ formState, ...props }: FieldProps<"customPayerName">) {
	return <TextField formState={formState} name="customPayerName" {...props} />;
}

/**
 * Field component for the `issueDate` field of the Payment model.
 */
export function IssueDate({ formState, label = "Payment Date", ...props }: FieldProps<"issueDate">) {
	return <DateTimeField formState={formState} name="issueDate" label={label} precision="day" {...props} />;
}

/**
 * Field component for the `paymentMethod` field of the Payment model.
 */
export function PaymentMethod({ formState, ...props }: FieldProps<"paymentMethod">) {
	return (
		<RadioField
			formState={formState}
			name="paymentMethod"
			options={PaymentFormValues.paymentMethodOptions}
			blankValue={null}
			{...props}
		/>
	);
}

/**
 * Field component for the `isInLocalCurrency` field of the Payment model.
 */
export function IsInLocalCurrency({
	formState,
	label = "Paid with alternate currency",
	...props
}: FieldProps<"isInLocalCurrency">) {
	return <BooleanField formState={formState} name="isInLocalCurrency" label={label} {...props} />;
}

/**
 * Field component for the `localCurrency` field of the Payment model.
 */
export function LocalCurrency({ formState, label = "Alternate Currency", ...props }: FieldProps<"localCurrency">) {
	return <CurrencyField formState={formState} name="localCurrency" label={label} {...props} />;
}

/**
 * Field component for the `notes` field of the Payment model.
 */
export function Notes({ formState, ...props }: FieldProps<"notes">) {
	return <TextField formState={formState} name="notes" multiLine optional Input={NotesInput} {...props} />;
}

function NotesInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={2} {...props} />;
}

export namespace GuardianPayer {
	export type SelectProps = FieldProps<"guardianPayerID"> &
		Omit<GuardianSelectField.SelectProps<PaymentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `guardianPayer` edge of the Payment model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <GuardianSelectField.Select formState={formState} name="guardianPayerID" {...props} />;
	}
}

export namespace PaymentLineItems {
	/**
	 * SubForm field component for the `paymentLineItems` edge of the Payment model.
	 */
	export function SubForm({
		formState,
		label = "Items",
		studentID,
		contractID,
		style,
		...props
	}: CreateFieldProps<"paymentLineItems" | "isInLocalCurrency" | "localCurrency"> & {
		studentID?: Student["id"];
		contractID?: Contract["id"];
	}) {
		const { isInLocalCurrency, localCurrency } = formState.formValues;
		const WrappedSubForm = React.useCallback(
			(props: SubFormRenderProps<PaymentLineItemFormValues.CreateInPayment>) => {
				return (
					<SubForm.SubForm
						studentID={studentID}
						contractID={contractID}
						isInLocalCurrency={isInLocalCurrency}
						localCurrency={localCurrency}
						{...props}
					/>
				);
			},
			[studentID, contractID, isInLocalCurrency, localCurrency],
		);

		const WrappedAddButton = React.useCallback(
			(
				props: AddSubFormButtonRenderProps<
					Pick<PaymentFormValues.Create, "paymentLineItems" | "isInLocalCurrency" | "localCurrency">,
					PaymentLineItemFormValues.CreateInPayment
				>,
			) => {
				return <SubForm.AddButton studentID={studentID} {...props} />;
			},
			[studentID],
		);

		return (
			<SubFormField
				formState={formState}
				name="paymentLineItems"
				label={label}
				blankItem={PaymentLineItemFormValues.blankItemInPayment}
				Content={SubForm.Content}
				Items={SubForm.Items}
				dividerElement={<Rule />}
				minItems={1}
				style={{ width: "100%", "--sub-form-field__value---padding": 0, ...style } as React.CSSProperties}
				{...props}
			>
				{WrappedSubForm}
				{WrappedAddButton}
			</SubFormField>
		);
	}

	const gridTemplateTracks = "minmax(10rem, 1fr) minmax(10rem, 1fr) minmax(10rem, 1fr) minmax(8rem, 1fr) 2rem";
	const gridGap = "1rem";

	SubForm.Content = function ({
		children,
	}: SubFormFieldContentProps<
		Pick<PaymentFormValues.Create, "paymentLineItems" | "isInLocalCurrency" | "localCurrency">,
		PaymentLineItemFormValues.CreateInPayment
	>) {
		return (
			<Column justify="spaced-start" className={styles["payment-line-items__content"]}>
				<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
					<Label>Student</Label>
					<Label>Contract</Label>
					<Label>Invoice</Label>
					<Label>Payment Amount</Label>
				</Grid>

				<Rule />

				{children}
			</Column>
		);
	};

	SubForm.Items = function ({
		children,
	}: SubFormFieldItemsProps<
		Pick<PaymentFormValues.Create, "paymentLineItems" | "isInLocalCurrency" | "localCurrency">,
		PaymentLineItemFormValues.CreateInPayment
	>) {
		return <React.Fragment>{children}</React.Fragment>;
	};

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		studentID,
		contractID,
		isInLocalCurrency,
		localCurrency,
	}: SubFormRenderProps<PaymentLineItemFormValues.CreateInPayment> & {
		studentID?: Student["id"];
		contractID?: Contract["id"];
		isInLocalCurrency: boolean;
		localCurrency: string;
	}) {
		return (
			<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
				<PaymentLineItemForm.CreateFieldsInPayment
					formState={formState}
					studentID={studentID}
					contractID={contractID}
					isInLocalCurrency={isInLocalCurrency}
					localCurrency={localCurrency}
				/>
				<Row justify="flex-end">
					<Button
						variant="tertiary"
						size="small"
						onClick={onRemoveClick}
						disabled={formState.disabled}
						className="button--icon"
					>
						<Icon.TrashSimple size={16} className="block" />
					</Button>
				</Row>
			</Grid>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		addItem,
		disabled,
		numItems,
		studentID,
	}: AddSubFormButtonRenderProps<
		Pick<PaymentFormValues.Create, "paymentLineItems" | "isInLocalCurrency" | "localCurrency">,
		PaymentLineItemFormValues.CreateInPayment
	> & { studentID?: Student["id"] }) {
		const { isOpen, open, close } = useOpenToggle(numItems === 0);

		const onClickAddCustomItem = React.useCallback(() => {
			onClick();
			close();
		}, [onClick, close]);

		const helperFormState = useFormState({
			initialFormValues: { studentID: studentID ?? null },
			formType: FormType.Standing,
			validationDisplayPolicy: ValidationDisplayPolicy.none,
		});

		const { loading, data } = useInvoicePaymentInformationListQuery({
			variables: {
				filters: {
					contractInstallment: [{ contract: [{ studentIDEQ: helperFormState.formValues.studentID }] }],
					statusEQ: InvoiceStatus.Issued,
				},
				order: { field: InvoiceOrderField.IssueDate, direction: OrderDirection.Asc },
			},
			skip: !helperFormState.formValues.studentID,
		});

		const onClickInvoice = React.useCallback(
			(invoice: Pick<Invoice, "id">) => {
				addItem({
					...PaymentLineItemFormValues.blankItemInPayment,
					invoiceID: invoice.id,
				});
				close();
			},
			[addItem, close],
		);

		return (
			<React.Fragment>
				<Rule />

				<Row justify="space-between" align="center">
					<Button variant="link" size="small" onClick={isOpen ? close : open} disabled={disabled}>
						<Conditional>
							<If condition={isOpen}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.X size={16} /> Cancel
								</Row>
							</If>
							<Else>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Plus size={16} /> Add Item
								</Row>
							</Else>
						</Conditional>
					</Button>

					<If condition={isOpen}>
						<StudentSelectField.Select formState={helperFormState} name="studentID" noHeader />
					</If>
				</Row>

				<If condition={isOpen}>
					<InvoicePaymentSelection
						isLoading={loading}
						invoices={data?.invoiceConnection.edges}
						onClickInvoice={onClickInvoice}
						disabled={disabled}
					/>

					<Button variant="link" size="small" onClick={onClickAddCustomItem} disabled={disabled}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.Plus size={16} /> Add Custom Item
						</Row>
					</Button>
				</If>
			</React.Fragment>
		);
	};
}
